import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  Download,
  FileText,
  Loader2,
  Calendar,
  Receipt,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { relatorioService, Relatorio } from "../../services/relatorioService";
import { formatDateFromDB } from "../../utils/dateUtils";
import { formatInvoiceNumber } from "../../utils/formatters";

export function Relatorios() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [relatorios, setRelatorios] = useState<Relatorio[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) navigate("/area-cliente");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    loadRelatorios();
  }, []);

  const loadRelatorios = async () => {
    setIsLoading(true);
    try {
      const result = await relatorioService.list();
      setRelatorios(result);
    } catch (error) {
      console.error("Erro ao carregar relatórios:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Relatórios | Portal do Cliente</title>
      </Helmet>

      <div className="min-h-screen">
        <div className="py-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Portal
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              <span className="bg-gradient-to-r from-brand to-blue-500 bg-clip-text text-transparent">
                Relatórios Técnicos
              </span>
            </h1>
            <p className="text-muted-foreground mt-1">
              Relatórios de atendimento publicados pela equipe
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl bg-card border border-border overflow-hidden"
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-brand animate-spin" />
              </div>
            ) : relatorios.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhum relatório publicado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Ticket</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden md:table-cell">Título</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden lg:table-cell">Fatura</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden lg:table-cell">Publicado em</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {relatorios.map((rel) => (
                      <tr key={rel.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-brand flex-shrink-0" />
                            <span className="text-foreground font-medium">{rel.ticket.codigo}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-muted-foreground text-sm">{rel.ticket.titulo}</span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          {rel.fatura ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
                              <Receipt className="w-3 h-3" />
                              {formatInvoiceNumber(rel.fatura.numero)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
                            <Calendar className="w-3 h-3" />
                            {formatDateFromDB(rel.dataGeracao)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => relatorioService.view(rel.id)}
                              className="p-2 rounded-lg bg-brand/10 text-brand hover:bg-brand/20 transition-colors"
                              title="Visualizar"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => relatorioService.download(rel.id, `relatorio-${rel.ticket.codigo}.pdf`)}
                              className="p-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
